import { useState, useEffect, useRef, useMemo, useCallback, useReducer } from 'react';
import { txApi, triggerDownload } from '../txApi.js';
import { C, inp, btn, Err, Spinner } from './_shared.jsx';
import SpreadsheetGrid, { uid, buildTaxString } from './_SpreadsheetGrid.jsx';
import { useWorkspace } from '../../../shared/workspace/WorkspaceContext.jsx';
import { usablePlatforms } from '../platformAccess.js';
import UtmGrid from './UtmGrid.jsx';

const lbl = (x = {}) => ({ fontSize: 10, fontWeight: 600, letterSpacing: '0.09em', color: C.muted, textTransform: 'uppercase', ...x });

// formatDate, buildTaxString, BatchGenerateModal, SpreadsheetGrid → moved to _SpreadsheetGrid.jsx
// ── Main BuilderPage ──────────────────────────────────────────────────────────
export default function BuilderPage({ me, onBack, initialClientId, initialPlatformId, initialLevelName, initialCampaignName, initialGroupId, initialGridRows, initialUtmSourceId, readOnly: readOnlyProp, targetLevel, targetUtm = false, onLevelChange, onGroupSaved }) {
  // A new campaign belongs to the active workspace; an existing one belongs to whichever
  // client it was created under, which arrives as initialClientId. Either way the client is
  // determined, not picked — there is no client control on this page.
  const { clientId: wsClientId } = useWorkspace();

  const [clients,      setClients]      = useState([]);
  const [platforms,    setPlatforms]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [err,          setErr]          = useState(null);
  const [selClient,    setSelClient]    = useState(initialClientId || wsClientId || '');
  const [selPlatform,  setSelPlatform]  = useState(initialPlatformId || '');
  const [campaignName, setCampaignName] = useState(initialCampaignName || '');
  // True while the campaign still carries the placeholder name autosave gave it. Cleared the
  // moment the user renames it, which is also what clears the flag server-side.
  const [isDraft,      setIsDraft]      = useState(false);
  const [levels,       setLevels]       = useState([]);
  const [lvlIdx,       setLvlIdx]       = useState(0);
  const [taxonomies,   setTaxonomies]   = useState({});
  const [dictValues,   setDictValues]   = useState({});
  const [gridRows,     setGridRows]     = useState({});
  const [taxLoading,   setTaxLoading]   = useState(false);
  const [isReadOnly,   setIsReadOnly]   = useState(!!initialGroupId); // existing campaigns open read-only; new ones open in edit mode
  // Set when this campaign sits on an archived taxonomy version: { num, status }. Such a
  // campaign is a historical record — it renders against the version it was built on and
  // has no editable state at all, so Edit is not offered rather than offered and refused.
  const [campaignVersion, setCampaignVersion] = useState(null);
  // Platforms this campaign uses that have since been switched off, globally or for this
  // client. Unlike a deleted level, the rows stay on screen — the campaign goes read-only.
  const [disabledPlatforms, setDisabledPlatforms] = useState([]);
  // The channels this campaign actually has rows on. The channel picker is also how a saved
  // multi-channel campaign is read, so a read-only one narrows to this set rather than being
  // disabled outright — see channelOptions.
  const [groupPlatformIds, setGroupPlatformIds] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(initialGroupId || null);
  // { [platformId]: { [levelName]: rows } } from a duplicate. A ref, not state: it is read
  // during the taxonomy load and never rendered from, and it must survive channel switches.
  const carriedRowsRef = useRef(initialGridRows || null);
  // { [platform::level]: { undo: [], redo: [] } } — see the historyRef prop on SpreadsheetGrid.
  const gridHistoryRef = useRef({});
  const [utmActive, setUtmActive] = useState(targetUtm);
  const [utmSetup, setUtmSetup] = useState(null);
  const [utmError, setUtmError] = useState('');
  const [utmStarting, setUtmStarting] = useState(false);
  const utmSessions = useRef({});
  const copiedUtm = useRef(new Set());
  const [utmCopying, setUtmCopying] = useState(false);
  const [utmCopyRetry, setUtmCopyRetry] = useState(0);
  useEffect(() => {
    if (!initialUtmSourceId || !activeGroupId || copiedUtm.current.has(activeGroupId)) return;
    setUtmCopying(true);
    txApi.duplicateUtm(activeGroupId, initialUtmSourceId).then(() => { copiedUtm.current.add(activeGroupId); setUtmError(''); })
      .catch(e => setUtmError('UTM copy failed: ' + e.message)).finally(() => setUtmCopying(false));
  }, [activeGroupId, initialUtmSourceId, utmCopyRetry]);
  useEffect(() => {
    let alive = true;
    setUtmSetup(null); setUtmError('');
    if (selClient) txApi.getUtmSetup(selClient).then(d => { if (alive) { setUtmSetup(d.setup); if (!d.setup) setUtmActive(false); } })
      .catch(e => { if (alive) { setUtmError(e.message); setUtmActive(false); } });
    return () => { alive = false; };
  }, [selClient]);
  useEffect(() => { setUtmActive(targetUtm); }, [targetUtm, targetLevel]);
  const startUtm = async () => {
    if (utmStarting) return;
    setUtmStarting(true);
    try {
      const d = campaignName.trim()
        ? await txApi.createCampaignGroup({ client_id: selClient, platform_id: selPlatform, name: campaignName.trim() })
        : await txApi.createDraftCampaignGroup({ client_id: selClient, platform_id: selPlatform });
      setActiveGroupId(d.group.id);
      if (!campaignName.trim()) { setCampaignName(d.group.name); setIsDraft(true); }
      onGroupSaved?.(d.group.id, 'utm/links');
    } catch (e) { setUtmError(e.message); } finally { setUtmStarting(false); }
  };
  const [exportModal,  setExportModal]  = useState(null); // null | { platforms: [{id,name}], selected: Set, exporting: bool }
  const [nameStatus,   setNameStatus]   = useState(null); // null | 'checking' | 'available' | 'taken'
  const nameCheckTimer = useRef(null);
  const [renameStatus, setRenameStatus] = useState(null); // null | 'saving' | 'saved' | 'error' — autosave status for renaming an EXISTING campaign
  const renameTimer = useRef(null);
  // Seeded from the initial name (mount-time value) so the first render doesn't fire a no-op PATCH.
  const lastSyncedNameRef = useRef(initialGroupId ? (initialCampaignName || '').trim() : null);

  // Sync level tab when URL level changes (browser back/forward)
  useEffect(() => {
    if (!targetLevel || !levels.length) return;
    const slug = s => s.toLowerCase().replace(/\s+/g, '-');
    const idx = levels.findIndex(lv => slug(lv.name) === targetLevel);
    if (idx >= 0 && idx !== lvlIdx) setLvlIdx(idx);
  }, [targetLevel, levels]);

  useEffect(() => {
    Promise.all([txApi.listClients(), txApi.listPlatforms()])
      .then(([c, p]) => { setClients(c.clients); setPlatforms(p.platforms); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, []);

  // New campaigns follow the workspace. TaxonomyApp leaves saved campaign views on a
  // workspace change, so their client remains fixed while they are mounted.
  useEffect(() => {
    if (initialClientId || activeGroupId) return;
    setSelClient(wsClientId || '');
    setSelPlatform('');
  }, [wsClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only usable platforms are offered for NEW work. A saved campaign's own platform is added
  // back below, so opening one whose platform was later disabled still renders its rows.
  const clientPlatforms = usablePlatforms(clients.find(c => c.id === selClient), platforms);

  useEffect(() => {
    if (!selClient || !selPlatform) { setLevels([]); setTaxonomies({}); setDictValues({}); setGridRows({}); return; }
    setTaxLoading(true); setErr(null); setLvlIdx(0); setGridRows({});

    txApi.getPlatformLevels(selPlatform).then(async d => {
      const lvls = d.levels || [];
      setLevels(lvls);
      if (!lvls.length) { setTaxLoading(false); return; }

      if (initialLevelName) {
        const idx = lvls.findIndex(lv => lv.name === initialLevelName);
        if (idx >= 0) setLvlIdx(idx);
      }

      // An existing campaign is read before its taxonomy, not after: its rows name the
      // version they were built on, and that version is what the grid has to render. Loading
      // the active taxonomy first and the rows second showed a campaign against a field set
      // it was never filled in against — new columns appeared, and every stored value went
      // to a field id that no longer existed, so the whole grid read as empty.
      let groupCampaigns = null;
      let levelVersions = null;
      if (activeGroupId) {
        // Only this channel's rows, and only the columns the grid renders — the request is
        // narrowed server-side rather than fetching every channel and filtering here.
        const gd = await txApi.getCampaignGroupRows(activeGroupId, selPlatform)
          .catch(() => ({ group: { campaigns: [] } }));
        const grp = gd.group || {};
        groupCampaigns = grp.campaigns || [];
        levelVersions = grp.level_versions || null;
        // The channels a campaign has rows on used to be derived from the rows themselves,
        // which is part of why they all had to be fetched. The server reports them directly.
        setGroupPlatformIds((grp.platform_ids || []).map(String).filter(Boolean));
        // Which of this campaign's platforms are no longer usable for its client. Computed by
        // the backend — a campaign's client is not always in the loaded client list, and
        // deriving it here would report "fine" in exactly the case that matters.
        setIsDraft(!!grp.is_draft);
        setDisabledPlatforms((grp.disabled_platform_ids || []).map((id, i) => ({
          id, name: (grp.disabled_platform_names || [])[i] || 'this platform',
        })));
        // Catches the case where Edit was clicked before the group finished loading.
        if ((grp.disabled_platform_ids || []).some(id => String(id) === String(selPlatform))) {
          setIsReadOnly(true);
        }
      } else {
        setDisabledPlatforms([]);
        setGroupPlatformIds([]);
      }

      // The version a level was built on is a property of the level, and now arrives once per
      // level instead of repeated on every row.
      const versionRows = levelVersions
        || (groupCampaigns || []);   // pre-slim shape, still carried per row
      const versionByLevel = {};
      versionRows.forEach(c => {
        const lvlName = c.level || lvls[0]?.name || '';
        if (c.taxonomy_version_id && !versionByLevel[lvlName]) {
          versionByLevel[lvlName] = c.taxonomy_version_id;
        }
      });
      // Archived on any level means the campaign as a whole is a historical record.
      const archived = versionRows.find(c => c.taxonomy_version_status === 'archived');
      setCampaignVersion(archived
        ? { num: archived.taxonomy_version_num, status: 'archived' }
        : null);
      if (archived) setIsReadOnly(true);

      const taxResults = await Promise.all(
        lvls.map(lv => txApi.getTaxonomy(selClient, selPlatform, lv.name, versionByLevel[lv.name])
          .then(r => [lv.name, r.taxonomy]).catch(() => [lv.name, null]))
      );
      const taxMap = Object.fromEntries(taxResults);
      setTaxonomies(taxMap);
      // Only show levels that have a taxonomy defined
      const lvlsWithTax = lvls.filter(lv => taxMap[lv.name] != null);
      setLevels(lvlsWithTax);

      const allDictIds = new Set();
      Object.values(taxMap).forEach(tax => {
        (tax?.fields || []).forEach(f => { if (f.dict_list_id) allDictIds.add(f.dict_list_id); });
      });
      if (allDictIds.size > 0) {
        const dictResults = await Promise.all(
          [...allDictIds].map(id => txApi.getDictValues(id).then(r => [id, (r.values || []).filter(v => !v.is_excluded)]).catch(() => [id, []]))
        );
        setDictValues(Object.fromEntries(dictResults));
      }

      // Rows carried in from a duplicate arrive per channel, because a campaign spans
      // channels and the builder only ever shows one. They stay available until the channel
      // they belong to has rows of its own: the copy is saved a channel at a time, so
      // switching away to fill in another and coming back must not arrive at an empty grid.
      const carried = carriedRowsRef.current?.[String(selPlatform)];
      if (carried && !(groupCampaigns || []).length) {
        setGridRows(carried);
      } else if (groupCampaigns) {
        const rowsByLevel = {};
        groupCampaigns.forEach(c => {
          const lvlName = c.level || lvls[0]?.name || '';
          // Keyed by field id, exactly as stored. Each id used to be aliased to Number(id) as
          // well, from when tx_fields.id was an integer; ids are UUIDs now, so every alias
          // collapsed to the single key "NaN", which the grid never read and the next save
          // wrote straight back to the database.
          const fv = { ...(typeof c.field_values === 'string' ? JSON.parse(c.field_values) : (c.field_values || {})) };
          if (!rowsByLevel[lvlName]) rowsByLevel[lvlName] = [];
          rowsByLevel[lvlName].push({ id: String(c.id), values: fv });
        });
        setGridRows(rowsByLevel);
      }

      setTaxLoading(false);
    }).catch(e => { setErr(e.message); setTaxLoading(false); });
  }, [selClient, selPlatform]);

  const setLevelRows = useCallback((levelName, updater) => {
    setGridRows(prev => ({ ...prev, [levelName]: typeof updater === 'function' ? updater(prev[levelName] || []) : updater }));
  }, []);

  // Debounced campaign name uniqueness check (only for new campaigns)
  const checkName = useCallback((name, clientId) => {
    clearTimeout(nameCheckTimer.current);
    if (!name.trim() || !clientId || activeGroupId) { setNameStatus(null); return; }
    setNameStatus('checking');
    nameCheckTimer.current = setTimeout(() => {
      txApi.checkCampaignGroupName(name.trim(), clientId)
        .then(d => setNameStatus(d.available ? 'available' : 'taken'))
        .catch(() => setNameStatus(null));
    }, 350);
  }, [activeGroupId]);

  // Autosave a rename for an EXISTING campaign (new campaigns get their name set at creation,
  // once the first grid row autosaves). Debounced like a cell edit; `flush=true` saves immediately
  // (used on blur / navigating away), matching the grid's autosave behavior.
  const saveNameNow = useCallback((name) => {
    if (!activeGroupId) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === lastSyncedNameRef.current) return;
    setRenameStatus('saving');
    txApi.updateCampaignGroup(activeGroupId, { name: trimmed })
      // Naming a draft is what promotes it, server-side and here.
      .then(() => { lastSyncedNameRef.current = trimmed; setRenameStatus('saved'); setIsDraft(false); })
      .catch(() => setRenameStatus('error'));
  }, [activeGroupId]);

  const pendingNameRef = useRef(null); // name queued for the debounced save, or null if nothing pending
  const scheduleNameSave = useCallback((name, flush = false) => {
    clearTimeout(renameTimer.current);
    if (!activeGroupId) return;
    if (flush) { pendingNameRef.current = null; saveNameNow(name); }
    else {
      pendingNameRef.current = name;
      renameTimer.current = setTimeout(() => { pendingNameRef.current = null; saveNameNow(name); }, 1500);
    }
  }, [activeGroupId, saveNameNow]);

  // Flush a pending rename if the user navigates away mid-debounce, instead of dropping it.
  const saveNameNowRef = useRef(saveNameNow);
  useEffect(() => { saveNameNowRef.current = saveNameNow; }, [saveNameNow]);
  useEffect(() => () => {
    clearTimeout(renameTimer.current);
    if (pendingNameRef.current != null) saveNameNowRef.current(pendingNameRef.current);
  }, []);

  const openExportModal = async () => {
    if (!activeGroupId) return;
    try {
      const gd = await txApi.getCampaignGroup(activeGroupId);
      const campaigns = (gd.group || {}).campaigns || [];
      // Derive unique platforms from campaign rows
      const seen = new Map();
      campaigns.forEach(c => {
        if (c.platform_id && !seen.has(String(c.platform_id))) {
          // Find the platform name from the loaded platforms list
          const p = platforms.find(pl => String(pl.id) === String(c.platform_id));
          seen.set(String(c.platform_id), { id: String(c.platform_id), name: p?.name || c.platform_name || String(c.platform_id) });
        }
      });
      ((gd.group || {}).utm_platforms || []).forEach(p => {
        if (!seen.has(String(p.platform_id))) seen.set(String(p.platform_id), { id: String(p.platform_id), name: p.platform_name });
      });
      const plats = [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
      if (!plats.length) { alert('No platform data found for this campaign.'); return; }
      setExportModal({ platforms: plats, selected: new Set(plats.map(p => p.id)), exporting: false });
    } catch (e) {
      alert('Could not load platforms: ' + e.message);
    }
  };

  const runExport = async () => {
    if (!exportModal || exportModal.exporting) return;
    setExportModal(prev => ({ ...prev, exporting: true }));
    for (const p of exportModal.platforms) {
      if (!exportModal.selected.has(p.id)) continue;
      try {
        const blob = await txApi.exportCampaignGroup(activeGroupId, p.id, p.name);
        const safeName = campaignName.replace(/\s+/g, '_').replace(/[^\w_-]/g, '').slice(0, 40);
        const safePlat = p.name.replace(/\s+/g, '_').replace(/[^\w_-]/g, '').slice(0, 20);
        triggerDownload(blob, `${safeName}_${safePlat}.xlsx`);
        await new Promise(r => setTimeout(r, 300)); // stagger downloads
      } catch (e) {
        alert(`Export failed for ${p.name}: ${e.message}`);
      }
    }
    setExportModal(null);
  };

  const isArchived = campaignVersion?.status === 'archived';
  const lockedPlatform = disabledPlatforms.find(p => String(p.id) === String(selPlatform)) || null;
  // Two independent reasons a campaign cannot be edited, and both can hold at once. They are
  // reported separately because they have separate remedies.
  const isLocked = isArchived || !!lockedPlatform;
  // A saved campaign must be able to select its own platform or the grid renders nothing, so
  // a disabled platform is put back into the list for this campaign only.
  const editableChannels = lockedPlatform && !clientPlatforms.some(p => String(p.id) === String(selPlatform))
    ? [...clientPlatforms, { id: selPlatform, name: lockedPlatform.name, disabled: true }]
    : clientPlatforms;

  // A read-only campaign is a record, not a form. The picker still moves between the channels
  // this campaign HAS rows on — that is how a multi-channel campaign is read, and the grid
  // reloads that channel's saved rows — but it must not offer channels the campaign was never
  // built on: picking one swapped the grid onto a foreign taxonomy underneath a banner saying
  // nothing here can be edited. Narrowed rather than disabled, so nothing becomes unreadable.
  const channelOptions = isReadOnly && groupPlatformIds.length
    ? groupPlatformIds.map(id => {
        const known = editableChannels.find(p => String(p.id) === String(id));
        if (known) return known;
        // A channel switched off since: named from the group's own list so the option still
        // reads as itself rather than as a bare id.
        const off = disabledPlatforms.find(p => String(p.id) === String(id));
        return { id, name: off?.name || 'Unavailable channel', disabled: true };
      })
    : editableChannels;

  const channelFixed = !selClient || !channelOptions.length
                       || (isReadOnly && channelOptions.length < 2);
  const totalRows = levels.reduce((acc, lv) => acc + (gridRows[lv.name] || []).length, 0);
  const curLevel  = levels[lvlIdx];
  const curTax    = curLevel ? taxonomies[curLevel.name] : null;
  const platform  = platforms.find(p => p.id === selPlatform);

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', padding: 0 }}>
            ← Campaigns
          </button>
        )}
        <div style={{ flex: 1 }} />
        {isReadOnly && !isLocked && (
          <button onClick={() => setIsReadOnly(false)} style={btn('primary', { fontSize: 12, padding: '7px 16px' })}>✎ Edit</button>
        )}
      </div>
      <Err msg={err} />

      {lockedPlatform && (
        <div style={{
          marginBottom: 18, fontSize: 12, color: C.warn, background: C.brassWash,
          border: `1px solid ${C.brass}33`, borderRadius: 8, padding: '8px 10px',
        }}>
          <b>This campaign is disabled because {lockedPlatform.name} is disabled.</b> Its rows
          are shown as they were saved and cannot be edited. Reactivating the platform, or
          re-assigning it to this client in Settings, makes them editable again.
        </div>
      )}

      {isArchived && (
        <div style={{
          marginBottom: 18, fontSize: 12, color: C.warn, background: C.brassWash,
          border: `1px solid ${C.brass}33`, borderRadius: 8, padding: '8px 10px',
        }}>
          <b>Built on taxonomy v{campaignVersion.num}, which is no longer active.</b> It is
          shown as it was created and cannot be edited. Duplicate it to carry its values onto
          the current taxonomy.
        </div>
      )}

      {/* Campaign name and channel share one row: together they are the campaign's identity,
          and the client is not restated here — it is the workspace the top bar already names.
          Read-only, the name is set as a title rather than repeated in a box it cannot be
          typed into; the channel stays a picker either way, since it is how a multi-channel
          campaign is read. */}
      <div style={{
        display: 'flex', gap: 20, marginBottom: 24, flexWrap: 'wrap',
        alignItems: 'flex-end',
        ...(isReadOnly && campaignName
          ? { paddingBottom: 16, borderBottom: `1px solid ${C.border}` }
          : null),
      }}>
        {!isReadOnly ? (
          <div style={{ flex: '2 1 320px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={lbl()}>Campaign name</span>
              {/* The rows are saved either way; what is missing is a name of the user's own,
                  and this is the field that supplies it. */}
              {isDraft && (
                <span className="o-badge" title="Saved under a placeholder name — rename it to finish">
                  Draft
                </span>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                value={campaignName}
                onChange={e => {
                  setCampaignName(e.target.value);
                  checkName(e.target.value, selClient);
                  scheduleNameSave(e.target.value);
                }}
                onBlur={e => scheduleNameSave(e.target.value, true)}
                placeholder="Enter campaign name…"
                style={inp({ fontSize: 14, paddingRight: 120,
                  borderColor: nameStatus === 'taken' ? C.danger : nameStatus === 'available' ? C.success : undefined })}
              />
              {nameStatus && !activeGroupId && (
                <span style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 11, fontWeight: 600,
                  color: nameStatus === 'checking' ? C.muted : nameStatus === 'available' ? C.success : C.danger,
                }}>
                  {nameStatus === 'checking' ? '…checking' : nameStatus === 'available' ? '✓ Available' : '✗ Already exists'}
                </span>
              )}
              {activeGroupId && renameStatus && (
                <span style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 11, fontWeight: 600,
                  color: renameStatus === 'saving' ? C.muted : renameStatus === 'saved' ? C.success : C.danger,
                }}>
                  {renameStatus === 'saving' ? 'Saving…' : renameStatus === 'saved' ? '✓ Saved' : '✗ Save failed'}
                </span>
              )}
            </div>
          </div>
        ) : campaignName ? (
          <div style={{ minWidth: 0, marginRight: 'auto', paddingBottom: 1 }}>
            <div style={lbl({ marginBottom: 5 })}>Campaign name</div>
            <div style={{
              fontFamily: C.display, fontSize: '1.25rem', fontWeight: 600,
              letterSpacing: '-.015em', lineHeight: 1.2, color: C.text,
              overflowWrap: 'anywhere',
            }}>
              {campaignName}
            </div>
          </div>
        ) : null}

        {/* Channel and Export are one right-hand cluster so their bottoms line up with the
            name. The select carries its own caret — `appearance:none` removes the native
            one, and without a replacement it reads as a text box, not a picker. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <div style={{ width: 220 }}>
            <div style={lbl({ marginBottom: 6 })}>Channel</div>
            <div style={{ position: 'relative' }}>
              <select value={selPlatform} onChange={e => setSelPlatform(e.target.value)}
                disabled={channelFixed}
                style={inp({
                  appearance: 'none', paddingRight: 30, fontSize: 13, fontWeight: 500,
                  cursor: channelFixed ? 'default' : 'pointer',
                  background: channelFixed ? C.s2 : C.surface,
                  color: channelFixed ? C.dim : C.text,
                })}>
                {/* No empty choice on a record: clearing the channel would blank a grid that
                    cannot be edited back into existence. */}
                {(!isReadOnly || !selPlatform) && <option value="">— Select channel —</option>}
                {channelOptions.map(p => (
                  <option key={p.id} value={p.id}>{p.name}{p.disabled ? ' — disabled' : ''}</option>
                ))}
              </select>
              {!channelFixed && (
                <span aria-hidden="true" style={{
                  position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 9, color: C.muted, pointerEvents: 'none',
                }}>▼</span>
              )}
            </div>
          </div>
          {activeGroupId && (
            <button onClick={openExportModal}
              style={btn('default', { fontSize: 12, padding: '9px 14px' })}>⬇ Export</button>
          )}
        </div>
      </div>

      {/* Level tabs */}
      {selPlatform && !taxLoading && (levels.length > 0 || utmSetup) && (
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: 24, gap: 2 }}>
          {levels.map((lv, i) => {
            const cnt = (gridRows[lv.name] || []).filter(r => buildTaxString(taxonomies[lv.name], r.values, dictValues)).length;
            return (
              <button key={lv.id} onClick={() => { setUtmActive(false); setLvlIdx(i); onLevelChange?.(lv.name.toLowerCase().replace(/\s+/g, '-')); }}
                style={{ background: 'transparent', border: 'none', borderBottom: `2px solid ${!utmActive && lvlIdx === i ? C.brass : 'transparent'}`, color: !utmActive && lvlIdx === i ? C.text : C.muted, padding: '10px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', fontFamily: 'inherit', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, marginBottom: -1 }}>
                <span style={{ fontSize: 11, opacity: 0.5 }}>{i + 1}.</span>
                {lv.name}
                {cnt > 0 && <span style={{ fontFamily: C.mono, color: C.brass, fontSize: 10, fontWeight: 600 }}>{cnt}</span>}
              </button>
            );
          })}
          {utmSetup && <button className="o-subtab" aria-current={utmActive ? 'page' : undefined}
            onClick={() => { setUtmActive(true); onLevelChange?.('utm/links'); }}>UTM</button>}
        </div>
      )}
      {utmError && <div className="o-hint">UTM unavailable: {utmError}
        {initialUtmSourceId && !copiedUtm.current.has(activeGroupId) && <button className="o-btn" disabled={utmCopying} onClick={() => setUtmCopyRetry(n => n + 1)}>Retry UTM copy</button>}
      </div>}
      {!taxLoading && utmActive && utmSetup && selPlatform && (
        utmCopying ? <div className="o-state">Copying UTM rows…</div> : activeGroupId ? <UtmGrid key={`${selClient}:${activeGroupId}:${selPlatform}`} groupId={activeGroupId}
          platformId={selPlatform} clientId={selClient} readOnly={isReadOnly || isLocked || !!readOnlyProp}
          sessions={utmSessions} />
          : <div className="o-state">Save taxonomy rows first, or start UTM rows for this campaign.
              <button className="o-btn o-btn-primary" disabled={utmStarting || nameStatus === 'taken' || !!readOnlyProp} onClick={startUtm}>{utmStarting ? 'Starting…' : 'Start UTM rows'}</button></div>
      )}

      {taxLoading && <Spinner />}

      {!utmActive && !taxLoading && selPlatform && !levels.length && (
        <div style={{ color: C.muted, fontSize: 13, padding: '40px 0', textAlign: 'center' }}>No levels configured for this platform.</div>
      )}
      {!utmActive && !taxLoading && curLevel && !curTax && (
        <div style={{ color: C.muted, fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
          No taxonomy defined for <b style={{ color: C.text }}>{curLevel.name}</b>. Set it up in Taxonomy Setup.
        </div>
      )}
      {!utmActive && !taxLoading && curLevel && curTax && (
        <SpreadsheetGrid
          key={`${selClient}-${selPlatform}-${curLevel.name}`}
          taxonomy={curTax}
          dictValues={dictValues}
          rows={gridRows[curLevel.name] || []}
          setRows={updater => setLevelRows(curLevel.name, updater)}
          clientId={selClient}
          platformId={selPlatform}
          levelName={curLevel.name}
          readOnly={isReadOnly}
          campaignName={campaignName}
          activeGroupId={activeGroupId}
          onGroupCreated={(gid, generatedName) => {
            setActiveGroupId(gid);
            setNameStatus(null);
            // A draft arrives with a name the server generated. Showing it is what makes the
            // campaign feel saved rather than nameless, and editing it is the rename that
            // promotes the draft.
            if (generatedName && !campaignName.trim()) {
              setCampaignName(generatedName);
              lastSyncedNameRef.current = generatedName;
              setIsDraft(true);
            }
            onGroupSaved?.(gid);
          }}
          onSaved={() => {}}
          nameBlocked={!activeGroupId && nameStatus === 'taken'}
          // Undo history is owned here so it survives the remount that a level or channel
          // switch forces on the grid — keyed per sheet, since an undo restores the rows it
          // was recorded against.
          historyRef={gridHistoryRef}
          historyKey={`${selPlatform}::${curLevel.name}`}
        />
      )}

      {!selClient && (
        <div className="o-state" style={{ padding: '80px 0' }}>
          No workspace selected. Pick a client from the workspace selector in the top bar.
        </div>
      )}

      {/* ── Export modal ── */}
      {exportModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !exportModal.exporting && setExportModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: '24px 28px', width: 360, maxWidth: '90vw',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>Export by Platform</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 18 }}>
              One file per platform, named <em>{campaignName || 'Campaign'}_{'{Platform}'}.xlsx</em>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {exportModal.platforms.map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: C.text }}>
                  <input type="checkbox"
                    checked={exportModal.selected.has(p.id)}
                    onChange={e => setExportModal(prev => {
                      const s = new Set(prev.selected);
                      e.target.checked ? s.add(p.id) : s.delete(p.id);
                      return { ...prev, selected: s };
                    })}
                    style={{ accentColor: C.ink, width: 15, height: 15 }}
                  />
                  {p.name}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={btn('ghost', { fontSize: 13 })} onClick={() => setExportModal(null)} disabled={exportModal.exporting}>Cancel</button>
              <button style={btn('primary', { fontSize: 13, padding: '7px 20px' })}
                onClick={runExport}
                disabled={exportModal.exporting || exportModal.selected.size === 0}>
                {exportModal.exporting ? 'Exporting…' : `Export ${exportModal.selected.size} file${exportModal.selected.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
